import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Sparkle, Crosshair, History, FileText, ChevronDown, Bell } from "lucide-react";
import PasswordInput from "@/components/julie/PasswordInput";
import { enablePushNotifications, pushPermission } from "@/lib/push";
import { getProfile, saveProfile, computeAge, capName, capFirst, initials, avatarImgStyle } from "@/lib/profile";
import { freePeriodHome } from "@/lib/freemium";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { flag: "🇫🇷", code: "+33", name: "France" },
  { flag: "🇧🇪", code: "+32", name: "Belgique" },
  { flag: "🇨🇭", code: "+41", name: "Suisse" },
  { flag: "🇱🇺", code: "+352", name: "Luxembourg" },
  { flag: "🇲🇨", code: "+377", name: "Monaco" },
  { flag: "🇨🇦", code: "+1", name: "Canada" },
  { flag: "🇩🇪", code: "+49", name: "Allemagne" },
  { flag: "🇮🇹", code: "+39", name: "Italie" },
  { flag: "🇪🇸", code: "+34", name: "Espagne" },
  { flag: "🇬🇧", code: "+44", name: "Royaume-Uni" },
  { flag: "🇲🇦", code: "+212", name: "Maroc" },
  { flag: "🇹🇳", code: "+216", name: "Tunisie" },
  { flag: "🇩🇿", code: "+213", name: "Algérie" },
];

const splitPhone = (v) => {
  if (!v) return { code: "+33", number: "" };
  const [first, ...rest] = v.split(" ");
  return first.startsWith("+") ? { code: first, number: rest.join(" ") } : { code: "+33", number: v };
};

export default function Profil() {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [infos, setInfos] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0, scale: 1 });
  const [usage, setUsage] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  // Notifications du matin : état de la permission sur CET appareil
  const [notifPerm, setNotifPerm] = useState(pushPermission);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;

  // Carte cadeau : le code reçu par e-mail crédite 30 jours d'Instants illimités
  const [giftCode, setGiftCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const redeemGift = async () => {
    if (!giftCode.trim() || redeeming) return;
    setRedeeming(true);
    try {
      const token = localStorage.getItem("rav-token");
      const { data } = await axios.post(`${API}/gifts/redeem`, { code: giftCode.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast(`${data.message} Un mois d'Instants illimités vous attend.`);
      setGiftCode("");
    } catch (err) {
      toast(err.response?.data?.detail || "Ce code n'a pas pu être activé.");
    } finally {
      setRedeeming(false);
    }
  };

  const enableNotifs = async () => {
    const res = await enablePushNotifications();
    setNotifPerm(pushPermission());
    if (res === "granted") toast("Notifications activées — chaque matin, un petit message pour vous. 🤎");
    else if (res === "no-sw-push") toast("Notifications activées dans l'app. Sur iPhone : installez l'app sur l'écran d'accueil pour les recevoir même app fermée.");
    else if (res === "denied") toast("Les notifications sont refusées — modifiable dans les réglages de l'appareil.");
    else toast("Les notifications ne sont pas disponibles sur cet appareil.");
  };
  const photoInput = useRef(null);
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  const clampPos = (v) => Math.max(-50, Math.min(50, v));

  const openAdjust = () => {
    const p = user?.photo_pos || {};
    setPos({ x: p.x || 0, y: p.y || 0, scale: p.scale || 1 });
    setAdjusting(true);
  };

  const onPhotoPointerDown = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPhotoPointerMove = (e) => {
    if (!dragRef.current) return;
    const size = frameRef.current?.offsetWidth || 240;
    const dx = ((e.clientX - dragRef.current.sx) / size) * 100;
    const dy = ((e.clientY - dragRef.current.sy) / size) * 100;
    setPos((p) => ({ ...p, x: clampPos(dragRef.current.ox + dx), y: clampPos(dragRef.current.oy + dy) }));
  };
  const onPhotoPointerUp = () => {
    dragRef.current = null;
  };

  const savePhotoPos = async () => {
    const token = localStorage.getItem("rav-token");
    try {
      const { data } = await axios.put(`${API}/users/me/photo-position`, pos, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser((u) => ({ ...u, photo_pos: data.photo_pos }));
      setAdjusting(false);
      toast("Votre photo est recadrée.");
    } catch {
      toast("Le cadrage n'a pas pu être enregistré.");
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const token = localStorage.getItem("rav-token");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/users/me/photo`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await axios.put(`${API}/users/me/photo-position`, { x: 0, y: 0, scale: 1 }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
      setUser((u) => ({ ...u, photo_url: data.photo_url, photo_pos: null }));
      toast("Votre photo est enregistrée — ajustez-la si besoin.");
    } catch (err) {
      toast(err.response?.data?.detail || "La photo n'a pas pu être envoyée.");
    } finally {
      setUploading(false);
    }
  };

  const flagOf = (code) => COUNTRIES.find((c) => c.code === code)?.flag || "";
  const dateFr = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  // La fiche cliente se pré-remplit avec tout ce qui est connu
  useEffect(() => {
    if (!user) return;
    const mobile = splitPhone(user.phone_mobile);
    const landline = splitPhone(user.phone_landline);
    setInfos({
      name: user.name || "",
      last_name: user.last_name || "",
      birthdate: user.birthdate || "",
      gender: user.gender || "",
      addressNumber: user.address_number || "",
      addressStreet: user.address_street || "",
      addressZip: user.address_zip || "",
      addressCity: user.address_city || "",
      mobileCode: mobile.code,
      mobileNumber: mobile.number,
      landlineCode: landline.code,
      landlineNumber: landline.number,
    });
  }, [user]);

  const saveInfos = async () => {
    if (!infos) return;
    setSaving(true);
    const token = localStorage.getItem("rav-token");
    try {
      const { data } = await axios.put(
        `${API}/auth/me`,
        {
          name: infos.name,
          last_name: infos.last_name,
          birthdate: infos.birthdate || null,
          gender: infos.gender || null,
          address_number: infos.addressNumber || null,
          address_street: infos.addressStreet || null,
          address_zip: infos.addressZip || null,
          address_city: infos.addressCity || null,
          phone_mobile: infos.mobileNumber ? `${infos.mobileCode} ${infos.mobileNumber}` : null,
          phone_landline: infos.landlineNumber ? `${infos.landlineCode} ${infos.landlineNumber}` : null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setUser(data.user);
      // synchronise le profil local (accueil, accords fille/garçon)
      const local = getProfile() || {};
      saveProfile({
        ...local,
        prenom: data.user.name || local.prenom,
        birthdate: data.user.birthdate || local.birthdate,
        age: data.user.age ?? computeAge(data.user.birthdate),
        gender: data.user.gender || local.gender,
      });
      toast("Vos informations sont enregistrées.");
      setEditing(false);
    } catch {
      toast("Une erreur est survenue. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("rav-token");
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API}/credits`, { headers })
      .then(({ data }) => setCredits(data))
      .catch(() => {});
    axios
      .get(`${API}/instants`, { headers })
      .then(({ data }) => setUsage(data.instants || []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem("rav-token");
    if (!token) {
      setUser(null);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data.user))
      .catch(() => {
        localStorage.removeItem("rav-token");
        setUser(null);
      });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("rav-token", data.access_token);
      if (data.user.role === "superadmin") {
        navigate("/admin");
        return;
      }
      // Règle : une connexion arrive toujours sur l'accueil
      navigate("/accueil");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className="rav-page" data-testid="profil-page">
        <motion.img
          src="/logo-accueil.webp"
          alt="Respire Aime Vibre"
          className="rav-page-logo"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
        {user === undefined ? null : user === null ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="rav-page-title" data-testid="login-title">Votre espace</h1>
            <p className="rav-page-sub">Retrouvez vos instants et votre douceur.</p>
            <form className="rav-form" onSubmit={submit} data-testid="login-form">
              <label className="rav-field">
                <span>Votre adresse e-mail</span>
                <input
                  type="email"
                  required
                  className="rav-input"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  autoComplete="email"
                />
              </label>
              <label className="rav-field">
                <span>Votre mot de passe</span>
                <PasswordInput
                  testId="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              {error && (
                <p className="rav-error" data-testid="login-error" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="rav-primary-button"
                data-testid="login-submit-button"
                disabled={loading}
              >
                <Sparkle size={16} strokeWidth={1.6} />
                {loading ? "Votre espace s’ouvre…" : "Se connecter"}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="rav-page-title" data-testid="profil-greeting">
              Bonjour{user.name ? ` ${user.name}` : ""}
            </h1>
            {credits && !credits.unlimited && (
              <p className="rav-gate-free" data-testid="profil-credits">
                {credits.trial
                  ? credits.remaining > 0
                    ? `Semaine découverte : ${credits.remaining} Instant${credits.remaining > 1 ? "s" : ""} offert${credits.remaining > 1 ? "s" : ""}${credits.trial_end ? ` jusqu'au ${credits.trial_end}` : ""}${credits.card_free ? " — sans carte bancaire" : ""}`
                    : "Vos 7 Instants offerts sont savourés 🤎 — découvrez nos offres ci-dessous"
                  : credits.remaining > 0
                  ? `Il vous reste ${credits.remaining} Instant${credits.remaining > 1 ? "s" : ""} offert${credits.remaining > 1 ? "s" : ""} ${freePeriodHome(credits.period)}`
                  : `Vos Instants offerts sont savourés — découvrez nos offres ci-dessous`}
              </p>
            )}
            {infos && !editing && (
              <div className="rav-admin-form rav-profil-form" data-testid="profil-summary">
                <div className="rav-avatar-top">
                  <button
                    type="button"
                    className="rav-avatar-btn"
                    data-testid="profil-avatar-button"
                    onClick={() => photoInput.current?.click()}
                    aria-label="Choisir ma photo de profil"
                  >
                    {user.photo_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${user.photo_url}`} style={avatarImgStyle(user)} alt="Votre photo de profil" />
                    ) : initials(user.name, user.last_name) ? (
                      <span className="rav-avatar-initials">{initials(user.name, user.last_name)}</span>
                    ) : (
                      <>
                        <Camera size={24} strokeWidth={1.5} />
                        {uploading ? "Envoi…" : "Ma photo"}
                      </>
                    )}
                  </button>
                  {user.photo_url && (
                    <button type="button" className="rav-text-link rav-avatar-adjust" data-testid="profil-avatar-adjust" onClick={openAdjust}>
                      Ajuster / centrer ma photo
                    </button>
                  )}
                  <input
                    ref={photoInput}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    data-testid="profil-avatar-input"
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  />
                </div>
                <h2 className="rav-section-title">Vos informations</h2>
                <div className="rav-profil-rows">
                  <p className="rav-profil-row" data-testid="profil-row-email">
                    <span>Adresse e-mail</span>
                    {user.email}
                  </p>
                  <p className="rav-profil-row" data-testid="profil-row-name">
                    <span>Prénom</span>
                    {infos.name || <em>À compléter</em>}
                  </p>
                  <p className="rav-profil-row" data-testid="profil-row-lastname">
                    <span>Nom</span>
                    {infos.last_name || <em>À compléter</em>}
                  </p>
                  <p className="rav-profil-row" data-testid="profil-row-birthdate">
                    <span>Date de naissance</span>
                    {infos.birthdate ? dateFr(infos.birthdate) : <em>À compléter</em>}
                  </p>
                  <p className="rav-profil-row" data-testid="profil-row-address">
                    <span>Adresse</span>
                    {[
                      [infos.addressNumber, infos.addressStreet].filter(Boolean).join(" "),
                      [infos.addressZip, infos.addressCity].filter(Boolean).join(" "),
                    ]
                      .filter(Boolean)
                      .join(", ") || <em>À compléter</em>}
                  </p>
                  <p className="rav-profil-row" data-testid="profil-row-mobile">
                    <span>Portable</span>
                    {infos.mobileNumber ? `${flagOf(infos.mobileCode)} ${infos.mobileCode} ${infos.mobileNumber}` : <em>À compléter</em>}
                  </p>
                </div>
                <button
                  type="button"
                  className="rav-secondary-button rav-session-btn rav-profil-btn"
                  data-testid="profil-edit-toggle"
                  onClick={() => setEditing(true)}
                >
                  Modifier mes informations
                </button>
              </div>
            )}

            {infos && editing && (
              <div className="rav-admin-form rav-profil-form" data-testid="profil-infos-form">
                <h2 className="rav-section-title">Vos informations</h2>
                <label className="rav-field">
                  <span>Votre adresse e-mail (identifiant de connexion)</span>
                  <input className="rav-input" data-testid="profil-email-readonly" type="email" value={user.email} disabled readOnly />
                </label>
                <label className="rav-field">
                  <span>Votre prénom</span>
                  <input
                    className="rav-input"
                    data-testid="profil-name"
                    value={infos.name}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setInfos({ ...infos, name: raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : "" });
                    }}
                  />
                </label>
                <label className="rav-field">
                  <span>Votre nom</span>
                  <input
                    className="rav-input"
                    data-testid="profil-lastname"
                    placeholder="Votre nom de famille"
                    value={infos.last_name}
                    onChange={(e) => setInfos({ ...infos, last_name: capName(e.target.value) })}
                  />
                </label>
                <label className="rav-field">
                  <span>Votre date de naissance</span>
                  <input
                    className="rav-input"
                    data-testid="profil-birthdate"
                    type="date"
                    min="1900-01-01"
                    max={new Date().toISOString().slice(0, 10)}
                    value={infos.birthdate}
                    onChange={(e) => setInfos({ ...infos, birthdate: e.target.value })}
                  />
                </label>
                <div className="rav-field">
                  <span>Votre adresse</span>
                  <div className="rav-address-grid">
                    <input
                      className="rav-input"
                      data-testid="profil-address-number"
                      placeholder="N°"
                      value={infos.addressNumber}
                      onChange={(e) => setInfos({ ...infos, addressNumber: e.target.value })}
                    />
                    <input
                      className="rav-input"
                      data-testid="profil-address-street"
                      placeholder="Rue"
                      value={infos.addressStreet}
                      onChange={(e) => setInfos({ ...infos, addressStreet: capFirst(e.target.value) })}
                    />
                    <input
                      className="rav-input"
                      data-testid="profil-address-zip"
                      placeholder="Code postal"
                      inputMode="numeric"
                      value={infos.addressZip}
                      onChange={(e) => setInfos({ ...infos, addressZip: e.target.value })}
                    />
                    <input
                      className="rav-input"
                      data-testid="profil-address-city"
                      placeholder="Ville"
                      value={infos.addressCity}
                      onChange={(e) => setInfos({ ...infos, addressCity: capFirst(e.target.value) })}
                    />
                  </div>
                </div>
                {[
                  { label: "Votre téléphone portable", key: "mobile", testId: "profil-mobile" },
                ].map((f) => (
                  <div className="rav-field" key={f.key}>
                    <span>{f.label}</span>
                    <div className="rav-phone-row">
                      <select
                        className="rav-input rav-phone-code"
                        data-testid={`${f.testId}-code`}
                        value={infos[`${f.key}Code`]}
                        onChange={(e) => setInfos({ ...infos, [`${f.key}Code`]: e.target.value })}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.code + c.name} value={c.code}>
                            {c.flag} {c.code}
                          </option>
                        ))}
                      </select>
                      <input
                        className="rav-input"
                        data-testid={f.testId}
                        type="tel"
                        placeholder={f.key === "mobile" ? "6 12 34 56 78" : "1 23 45 67 89"}
                        value={infos[`${f.key}Number`]}
                        onChange={(e) => setInfos({ ...infos, [`${f.key}Number`]: e.target.value })}
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="rav-primary-button"
                  data-testid="profil-save-button"
                  disabled={saving}
                  onClick={saveInfos}
                >
                  {saving ? "Enregistrement…" : "Enregistrer mes informations"}
                </button>
                <button
                  type="button"
                  className="rav-text-link"
                  data-testid="profil-edit-cancel"
                  onClick={() => setEditing(false)}
                >
                  Refermer sans enregistrer
                </button>
              </div>
            )}

            <div className="rav-profil-duo">
              <div className="rav-profil-panel" data-testid="profil-notifs-panel">
                <button
                  type="button"
                  className="rav-profil-panel-head"
                  data-testid="profil-notifs-toggle"
                  onClick={() => setOpenPanel(openPanel === "notifs" ? null : "notifs")}
                  aria-expanded={openPanel === "notifs"}
                >
                  <Bell size={17} strokeWidth={1.6} />
                  Notifications du matin
                  <ChevronDown size={16} strokeWidth={1.6} className={`rav-profil-chevron${openPanel === "notifs" ? " is-open" : ""}`} />
                </button>
                {openPanel === "notifs" && (
                  <div className="rav-profil-panel-body">
                    {notifPerm === "granted" ? (
                      <p className="rav-profil-panel-empty" data-testid="profil-notifs-on">
                        Les notifications sont activées : chaque matin, un petit message vous invite à prendre un Instant pour vous. 🤎
                      </p>
                    ) : (
                      <>
                        <p className="rav-profil-panel-empty" data-testid="profil-notifs-off">
                          Recevez chaque matin un petit message vous invitant à prendre un Instant pour vous — dans l'app, et sur votre téléphone si vous l'autorisez.
                        </p>
                        <button
                          type="button"
                          className="rav-primary-button"
                          data-testid="profil-notifs-enable"
                          onClick={enableNotifs}
                        >
                          🔔 Activer les notifications du matin
                        </button>
                        {isIOS && !isStandalone && (
                          <p className="rav-profil-panel-empty" data-testid="profil-notifs-ios-hint">
                            Sur iPhone : pour recevoir les messages même app fermée, installez d'abord l'app sur votre écran d'accueil (bouton Partager → « Sur l'écran d'accueil »).
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="rav-profil-panel" data-testid="profil-gift-panel">
                <button
                  type="button"
                  className="rav-profil-panel-head"
                  data-testid="profil-gift-toggle"
                  onClick={() => setOpenPanel(openPanel === "gift" ? null : "gift")}
                  aria-expanded={openPanel === "gift"}
                >
                  🎁 J'ai une carte cadeau
                  <ChevronDown size={16} strokeWidth={1.6} className={`rav-profil-chevron${openPanel === "gift" ? " is-open" : ""}`} />
                </button>
                {openPanel === "gift" && (
                  <div className="rav-profil-panel-body">
                    <p className="rav-profil-panel-empty">Saisissez le code reçu (ex. RAV-AB12CD) : 30 jours d'Instants illimités vous attendent.</p>
                    <input
                      className="rav-input"
                      data-testid="profil-gift-code"
                      placeholder="Votre code cadeau"
                      value={giftCode}
                      onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                      autoCapitalize="characters"
                    />
                    <button type="button" className="rav-primary-button" data-testid="profil-gift-redeem" disabled={redeeming} onClick={redeemGift}>
                      {redeeming ? "Activation…" : "Activer ma carte cadeau"}
                    </button>
                  </div>
                )}
              </div>
              <div className="rav-profil-panel" data-testid="profil-history-panel">
                <button
                  type="button"
                  className="rav-profil-panel-head"
                  data-testid="profil-history-toggle"
                  onClick={() => setOpenPanel(openPanel === "histoire" ? null : "histoire")}
                  aria-expanded={openPanel === "histoire"}
                >
                  <History size={17} strokeWidth={1.6} />
                  Historique d'utilisation
                  <ChevronDown size={16} strokeWidth={1.6} className={`rav-profil-chevron${openPanel === "histoire" ? " is-open" : ""}`} />
                </button>
                {openPanel === "histoire" && (
                  <div className="rav-profil-panel-body">
                    {usage.length === 0 ? (
                      <p className="rav-profil-panel-empty" data-testid="profil-history-empty">Aucune utilisation pour le moment.</p>
                    ) : (
                      usage.map((it, i) => (
                        <p className="rav-profil-panel-row" key={it.id || i} data-testid={`profil-history-item-${i}`}>
                          <span>{new Date(it.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</span>
                          <span>
                            Instant {it.minutes ? `${it.minutes} min` : ""}
                            {it.stopped_at_pct != null ? ` — stoppé à ${it.stopped_at_pct} %` : it.feeling_after ? ` — terminé` : ""}
                          </span>
                        </p>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="rav-profil-panel" data-testid="profil-invoices-panel">
                <button
                  type="button"
                  className="rav-profil-panel-head"
                  data-testid="profil-invoices-toggle"
                  onClick={() => setOpenPanel(openPanel === "factures" ? null : "factures")}
                  aria-expanded={openPanel === "factures"}
                >
                  <FileText size={17} strokeWidth={1.6} />
                  Mes factures (PDF)
                  <ChevronDown size={16} strokeWidth={1.6} className={`rav-profil-chevron${openPanel === "factures" ? " is-open" : ""}`} />
                </button>
                {openPanel === "factures" && (
                  <div className="rav-profil-panel-body">
                    <p className="rav-profil-panel-empty" data-testid="profil-invoices-empty">
                      Aucune facture pour le moment — elles apparaîtront ici après un achat.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="rav-profil-panel rav-qr-profil" data-testid="profil-share-panel">
              <img src="/qr-partage.png" alt="QR Code vers respireaimevibre.com" className="rav-qr-img" data-testid="profil-share-qr" />
              <p className="rav-qr-profil-text">
                Une amie cherche un moment pour elle ? Faites-lui scanner ce code — Respire Aime Vibre s'ouvre
                directement.
              </p>
            </div>

            <div className="rav-profil-actions">
              <button
                type="button"
                className="rav-secondary-button rav-session-btn rav-profil-btn"
                data-testid="profil-offers-link"
                onClick={() => navigate("/offres")}
              >
                Découvrir nos offres
              </button>
            </div>
            {user.role === "superadmin" && (
              <button
                type="button"
                className="rav-primary-button"
                data-testid="goto-admin-button"
                onClick={() => navigate("/admin")}
              >
                <Sparkle size={16} strokeWidth={1.6} />
                Accéder au Super Admin
              </button>
            )}
          </motion.div>
        )}
      </main>
      {adjusting && user?.photo_url && (
        <div className="rav-photo-modal" data-testid="photo-adjust-modal" onClick={() => setAdjusting(false)}>
          <div className="rav-photo-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="rav-section-title">Ajustez votre photo</h2>
            <p className="rav-photo-hint">Faites glisser la photo pour la déplacer, zoomez, puis enregistrez.</p>
            <div
              ref={frameRef}
              className="rav-photo-frame"
              data-testid="photo-adjust-frame"
              onPointerDown={onPhotoPointerDown}
              onPointerMove={onPhotoPointerMove}
              onPointerUp={onPhotoPointerUp}
              onPointerCancel={onPhotoPointerUp}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${user.photo_url}`}
                style={{ transform: `translate(${pos.x}%, ${pos.y}%) scale(${pos.scale})` }}
                alt="Aperçu du cadrage"
                draggable="false"
              />
            </div>
            <label className="rav-photo-zoom">
              Zoom
              <input
                type="range"
                min="1"
                max="3"
                step="0.05"
                value={pos.scale}
                data-testid="photo-adjust-zoom"
                onChange={(e) => setPos((p) => ({ ...p, scale: Number(e.target.value) }))}
              />
            </label>
            <div className="rav-photo-actions">
              <button
                type="button"
                className="rav-text-link"
                data-testid="photo-adjust-center"
                onClick={() => setPos((p) => ({ ...p, x: 0, y: 0 }))}
              >
                <Crosshair size={14} strokeWidth={1.6} /> Centrer
              </button>
              <button type="button" className="rav-text-link" data-testid="photo-adjust-change" onClick={() => photoInput.current?.click()}>
                Changer de photo
              </button>
              <button type="button" className="rav-text-link" data-testid="photo-adjust-close" onClick={() => setAdjusting(false)}>
                Annuler
              </button>
              <button type="button" className="rav-primary-button rav-photo-save" data-testid="photo-adjust-save" onClick={savePhotoPos}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
